import { makeAutoObservable } from "mobx"

import { AnswerData } from "@framework/types/search"
import { ExpertVerification } from "@framework/types/question"
import { SimpleUserData } from "@framework/types/user"
import { SimpleContentTypeData } from "@framework/types/content-manager"
import { DataSourceName } from "@framework/types/upload"

import SearchAnswersDataStore from "./search-answers-data.store"
import SearchAnswersContextStore from "./search-answers-context.store"

export class SearchCitationsStore {
  searchAnswersStore: SearchAnswersDataStore

  searchAnswersContextStore: SearchAnswersContextStore

  constructor(config: {
    searchAnswersStore: SearchAnswersDataStore
    searchAnswersContextStore: SearchAnswersContextStore
  }) {
    this.searchAnswersStore = config.searchAnswersStore
    this.searchAnswersContextStore = config.searchAnswersContextStore

    makeAutoObservable(this)
  }

  get searchAnswers() {
    return this.searchAnswersStore.allAnswers || []
  }

  get totalSources() {
    return this.searchAnswers.length
  }

  get citations(): SearchCitationData[] {
    return this.searchAnswers.map<SearchCitationData>((item, index) => {
      const isExpertAnswer = item.value?.source === "expert answer"
      const contentSourceName = item.value.source_type || null

      const defaultProps = {
        id: item.name,
        citationNumber: index + 1,
        description: (item.value as any).text ?? (item.value as any).value,
        answerSource: item.value,
        connectorName: item.value.connectorName ?? null,
        detailsLoadingError: null,
        sourceName: item.value.source_name,
        sourceURL: item.value.source,
        sourceType: item.value.source_type,
        isDocumentOld: !!item.value.isDocumentOld,
        documentAgeCutoff: item.value.documentAgeCutoff ?? null,
        displayContentId: item.value.displayContentId ?? null,
        isExternalSource: contentSourceName === "Web Research",
      }

      if (isExpertAnswer) {
        return {
          questionId: item.value.documentId ?? null,
          question: item.value?.source_name,
          isExpertAnswer: true,
          verifiedBy: [],
          ...defaultProps,
        }
      }

      return {
        contentId: item.value.documentId ?? null,
        url: Array.isArray(item.value.screenshots)
          ? item.value.screenshots.join(", ")
          : item.value.screenshots ?? "",
        isExpertAnswer: false,
        contentSourceName,
        ...defaultProps,
      }
    })
  }

  get citationsMap() {
    const { citations } = this
    return new Map(citations.map((it) => [it.id, it]))
  }

  get getByIndex() {
    const { citations } = this
    return (index: number): SearchCitationData | null => {
      return citations[index] ?? null
    }
  }

  get getById() {
    const { citationsMap } = this
    return (id: string): SearchCitationData | null => {
      return citationsMap.get(id) ?? null
    }
  }
}

export default SearchCitationsStore

export type BaseSearchCitation = {
  id: string
  connectorName?: string | null
  citationNumber: number
  description?: string
  answerSource?: AnswerData
  isExpertAnswer: boolean
  sourceURL?: string | null
  sourceName?: string | null
  url?: string
  detailsLoadingError?: string | null
  isDocumentOld: boolean
  documentAgeCutoff: number | null
  displayContentId: string | null
  isExternalSource?: boolean
}

export type ContentCitationData = {
  isExpertAnswer: false
  contentId?: string | null
  contentSourceName: DataSourceName | string | null
  connectedBy?: SimpleUserData
  contentType?: SimpleContentTypeData
  sourceType?: string
} & BaseSearchCitation

export type ExpertInsightCitationData = {
  questionId?: string | null
  question: string | null
  verifiedBy: ExpertVerification[]
  isExpertAnswer: true
} & BaseSearchCitation

export type SearchCitationData = ExpertInsightCitationData | ContentCitationData
